import { CommonModule } from '@angular/common';
import {
  Component,
  forwardRef,
  OnInit,
  TemplateRef,
  inject,
  viewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { NgxDrawCanvasComponent } from 'ngx-draw-canvas';
import { ok } from 'ngx-rx-alert';
import { NgxRxModalService } from 'ngx-rx-modal';
import { BehaviorSubject, Subject } from 'rxjs';

import { base64ToBlob } from '@alan-apps/utils';

import { SafePipe } from '../../pipes';
import { input } from '@angular/core';

@Component({
  selector: 'ngx-signature',
  standalone: true,
  imports: [
    CommonModule,
    NgxDrawCanvasComponent,
    FormsModule,
    SafePipe,
    MatButtonModule,
    MatIconModule,
  ],
  template: `
    <div
      class="w-40 h-20 border border-gray-400 rounded p-2 text-gray-400 overflow-hidden bg-cover bg-no-repeat bg-center relative"
      (click)="openSignature()"
      [ngClass]="disabled ? '' : 'cursor-pointer'"
      [style.background-image]="
        signatureUrl$
          | async
          | safe: 'background-image' : 'assets/img/default_page.png'
      "
    >
      @if ((signatureUrl$ | async) === null) {
        簽名
      } @else {
        @if (!disabled) {
          <button
            class="absolute right-1 top-1 border rounded-full border-gray-400 flex items-center justify-center aspect-square"
            type="button"
            (click)="reset($event)"
          >
            <mat-icon class="w-4 h-4 text-xs">clear</mat-icon>
          </button>
        }
      }
    </div>
    <ng-template #signatureTmpl let-complete="complete">
      <div class="border flex border-gray-400 rounded overflow-hidden">
        <ngx-draw-canvas [width]="600" [height]="300" />
      </div>
      <footer class="flex gap-2 mt-4">
        <button mat-raised-button color="accent" (click)="clear()">清除</button>
        <i class="flex-auto"></i>
        <button mat-raised-button color="primary" (click)="confirm(complete)">
          確定
        </button>
        <button mat-raised-button (click)="complete.next(null)">取消</button>
      </footer>
    </ng-template>
  `,
  styles: [':host { display: block; }'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NgxSignatureComponent),
      multi: true,
    },
  ],
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NgxSignatureComponent implements OnInit {
  private _modal = inject(NgxRxModalService);
  private _controlContainer = inject(ControlContainer, {
    optional: true,
    host: true,
    skipSelf: true,
  });
  drawCanvas = viewChild(NgxDrawCanvasComponent);

  signatureTmpl = viewChild<TemplateRef<HTMLDivElement>>('signatureTmpl');

  formControlName = input<string | undefined>();

  control?: AbstractControl<any, any> | null;

  signatureUrl$ = new BehaviorSubject<string | null>(null);

  get disabled() {
    return this.control?.disabled;
  }

  ngOnInit() {
    const formControlName = this.formControlName();
    if (!formControlName || !this._controlContainer.control) return;

    this.control = this._controlContainer.control.get(formControlName);
  }

  openSignature() {
    if (this.disabled || !this.signatureTmpl()) {
      return null;
    }

    return this._modal
      .open(this.signatureTmpl())
      .pipe(
        ok((blob: Blob) => {
          this.control?.patchValue(blob);

          const url = URL.createObjectURL(blob);
          this.signatureUrl$.next(url);
        }),
      )
      .subscribe();
  }

  confirm(complete: Subject<Blob>) {
    if (!this.drawCanvas()) return;

    const dataURL = this.drawCanvas().toDataURL('image/png', 0.5);
    if (!dataURL) return;

    const blob = base64ToBlob(dataURL);

    complete.next(blob);
  }

  reset($event: Event) {
    this.signatureUrl$.next(null);
    this.control?.patchValue(null);
    $event.preventDefault();
    $event.stopPropagation();
  }

  clear() {
    if (!this.drawCanvas()) return;

    this.drawCanvas().clear();
  }

  writeValue(url: string | Blob | null): void {
    if (url instanceof Blob) return;

    if (url !== this.signatureUrl$.value) {
      this.signatureUrl$.next(url);
    }
  }
  registerOnChange(fn: any): void {
    //
  }
  registerOnTouched(fn: any): void {
    //
  }
  setDisabledState?(isDisabled: boolean): void {
    //
  }
}
